import React, {
  createContext,
  FocusEvent,
  forwardRef,
  JSX,
  memo,
  PropsWithChildren,
  Ref,
  useContext,
  useEffect,
  useImperativeHandle
} from 'react';
import {
  DefaultValues,
  FieldErrors,
  FieldValues,
  FormState,
  Mode,
  Path,
  PathValue,
  Resolver,
  SetValueConfig,
  SubmitHandler,
  useForm,
  UseFormReturn,
  useWatch
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { FieldWatcherProps, FormRef } from './types/types';
import useWindow from '../Hooks/useWindow';

function FormFieldWatcher<DataType extends FieldValues>({
  control,
  onFormFieldsChange,
  setValue,
  isValid,
  isSubmitted
}: FieldWatcherProps<DataType>): null {
  const formFields = useWatch({ control }) as DataType;
  useEffect(() => {
    onFormFieldsChange(formFields, setValue, isValid, isSubmitted);
  }, [formFields, isValid, isSubmitted, setValue, onFormFieldsChange]);
  return null;
}

const MemoizedFormFieldWatcher = memo(
  FormFieldWatcher,
  (
    /* eslint-disable-next-line */
    prev: FieldWatcherProps<{ [x: string]: any }>,
    /* eslint-disable-next-line */
    next: FieldWatcherProps<{ [x: string]: any }>
  ) => {
    return (
      prev.control === next.control &&
      prev.onFormFieldsChange === next.onFormFieldsChange &&
      prev.setValue === next.setValue &&
      prev.isValid === next.isValid &&
      prev.isSubmitted === next.isSubmitted
    );
  }
) as typeof FormFieldWatcher;

interface Props<DataType extends FieldValues> {
  /* eslint-disable-next-line */
  validationSchema?: yup.AnyObjectSchema | yup.Lazy<any, any, any>;
  initialValues: DefaultValues<DataType>;
  id?: string;
  ariaLabelledBy?: string;
  legend?: string;
  withFormTag?: boolean;
  withFieldSet?: boolean;
  onSubmit?: SubmitHandler<DataType>;
  onFormBlur?: (e: FocusEvent<HTMLFormElement>) => void;
  onFormFieldsChange?: FieldWatcherProps<DataType>['onFormFieldsChange'];
  yupContext?: Record<string, unknown>;
  scrollToError?: boolean;
  mode?: Mode;
  reValidateMode?: Exclude<Mode, 'onTouched' | 'all'>;
  errorCallback?: (errors: FieldErrors<DataType>) => void;
  exposeSeparateFieldValue?: (fieldName: string, value: Record<string, unknown>) => void;
}

type Context<DataType extends FieldValues> = Partial<
  UseFormReturn<DataType, object> & {
    errors: FieldErrors<DataType>;
    exposeSeparateFieldValue?: Props<DataType>['exposeSeparateFieldValue'];
  }
>;

const FormContext = createContext({});

function Form<DataType extends FieldValues>(
  props: PropsWithChildren<Props<DataType>>,
  ref?: Ref<FormRef<DataType>>
): JSX.Element {
  const window = useWindow();
  const {
    id,
    ariaLabelledBy,
    children,
    legend,
    // this is confusing. props shouldn't have default values that work against the
    // expected default behavior (e.g. true, if variable is not passed)
    withFormTag = true,
    withFieldSet = false,
    validationSchema,
    onSubmit,
    initialValues,
    onFormFieldsChange,
    onFormBlur,
    yupContext,
    scrollToError,
    mode = 'onSubmit',
    reValidateMode = 'onChange',
    errorCallback,
    exposeSeparateFieldValue
  } = props;

  const resolver: Resolver<DataType> | undefined = validationSchema
    ? (yupResolver(
        /* eslint-disable-next-line */
        validationSchema as yup.ObjectSchema<any> | yup.Lazy<any, yup.AnyObject, any>
      ) as Resolver<DataType>)
    : undefined;

  const form = useForm<DataType>({
    ...(resolver
      ? {
          resolver
        }
      : {}),
    context: yupContext,
    defaultValues: initialValues,
    mode,
    reValidateMode
  });

  const { register, handleSubmit, control, setValue, getValues, trigger, reset, formState } = form;

  const { errors, isValid, isSubmitted } = formState;

  const resolveSubmit = async (): Promise<boolean> => {
    return new Promise(resolve => {
      const submitFn = handleSubmit(
        () => resolve(true),
        () => resolve(false)
      );
      submitFn();
    });
  };

  useImperativeHandle(ref, () => ({
    triggerSubmit: (): Promise<DataType> => {
      // eslint-disable-next-line
      return new Promise(async resolve => {
        if (await resolveSubmit()) {
          if (onSubmit) {
            onSubmit(getValues());
          }
          resolve(getValues());
        } else {
          resolve({} as DataType);
        }
      });
    },
    triggerSubmitWithoutValidation: (): Promise<DataType> => {
      // eslint-disable-next-line
      return new Promise(async resolve => {
        if (onSubmit) {
          onSubmit(getValues());
        }
        resolve(getValues());
      });
    },
    triggerFieldValidation: async (fieldName: string): Promise<boolean> => {
      return await trigger(fieldName as Path<DataType>);
    },
    resetForm: (values?: DataType): void => {
      const valWithId = values as ({ id?: string } & DataType) | undefined;
      const initValWithId = initialValues as ({ id?: string } & DataType) | undefined;
      if (valWithId && initValWithId) {
        valWithId.id = initValWithId.id;
      }
      reset(valWithId as DataType);
    },
    getCurrentValues: (): DataType => getValues(),
    setValue: (name: string, value: string, options?: SetValueConfig): void => {
      setValue(name as Path<DataType>, value as PathValue<DataType, Path<DataType>>, options);
    },
    getFormState: (): FormState<DataType> => formState
  }));

  useEffect(() => {
    if (Object.keys(errors).length > 0 && scrollToError) {
      const firstError = window?.document?.querySelectorAll('.form__error__top')[0];
      if (firstError) {
        firstError.scrollIntoView({
          behavior: 'smooth'
        });
      }
      if (errorCallback) {
        errorCallback(errors);
      }
    }
  }, [errors, scrollToError, errorCallback, window]);

  const onFormSubmit = (): ReturnType<typeof handleSubmit> => {
    return handleSubmit((values: DataType): void => {
      if (onSubmit) {
        onSubmit(values);
      }
    });
  };

  const content = withFieldSet ? (
    <fieldset>
      {legend && <legend>{legend}</legend>}
      {children}
    </fieldset>
  ) : (
    children
  );

  return (
    <FormContext.Provider
      value={{
        register,
        handleSubmit,
        control,
        errors,
        setValue,
        getValues,
        exposeSeparateFieldValue,
        formState
      }}
    >
      {withFormTag ? (
        <form
          id={id}
          onSubmit={onFormSubmit()}
          onBlur={onFormBlur}
          aria-labelledby={ariaLabelledBy}
        >
          {content}
        </form>
      ) : (
        content
      )}

      {onFormFieldsChange ? (
        <MemoizedFormFieldWatcher
          setValue={setValue}
          control={control}
          isValid={isValid}
          isSubmitted={isSubmitted}
          onFormFieldsChange={onFormFieldsChange}
        />
      ) : null}
    </FormContext.Provider>
  );
}

export const useFormContext = <DataType extends FieldValues>(): Context<DataType> => {
  const context = useContext<Context<DataType>>(FormContext);
  if (!context) {
    throw new Error('FormContext not usable outside of context provider');
  }
  return context;
};

export default forwardRef(Form) as <DataType extends FieldValues>(
  props: PropsWithChildren<Props<DataType>> & {
    ref?: Ref<FormRef<DataType>>;
  }
) => JSX.Element;
